import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  DataRoomShareableLink,
  ShareableLinkAnalyticsMember,
} from 'models/ShareableLink';
import useGetAnalytics from './useGetAnalytics';
import useInfinitePagination from 'hooks/useInfinitePagination';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import InfiniteScroll from 'components/InfiniteScroll';
import SecureLinkAnalyticsHeader from './components/SecureLinkAnalyticsHeader';
import SecureLinkEmpty from './components/SecureLinkEmpty';
import { getIsArrayEmpty } from 'utils';
import SecureLinkMembers from './components/SecureLinkMembers';

import './SecureLinkAnalytics.styles.scss';

const MEMBERS_PAGE_LIMIT = '10';

type SecureLinkAnalyticsProps = {
  className?: string;
  link: DataRoomShareableLink;
};

const SecureLinkAnalytics: React.FC<SecureLinkAnalyticsProps> = (props) => {
  const { className, link } = props;

  const classes = classNames('secure-link-analytics', className);

  const { _id, name } = link || {};

  const { t } = useTranslation();

  const { analytics, isLoading } = useGetAnalytics(_id);

  const {
    items: members,
    loading: isMembersLoading,
    onContainerScrolled: onMembersScrolled,
  } = useInfinitePagination<ShareableLinkAnalyticsMember>({
    condition: !!_id,
    resetDeps: [_id],
    dependencies: [_id],
    makeRequest: (currentPage) =>
      dataRoomApi.shareableLinks
        .getAnalyticsMembers(_id, {
          $page: currentPage.toString(),
          $limit: MEMBERS_PAGE_LIMIT,
        })
        .then(({ data }) => data),
  });

  const isEmpty = !isMembersLoading && getIsArrayEmpty(members);

  return (
    <div className={classes}>
      <p>{t('secureLinkAnalytics', { secureLink: name })}</p>
      <p>{t('secureLinkAnalyticsDescription')}</p>

      <SecureLinkAnalyticsHeader {...analytics} isLoading={isLoading} />

      {isEmpty ? (
        <SecureLinkEmpty link={link} />
      ) : (
        <InfiniteScroll
          localScroll
          className="secure-link-analytics__members-scroll"
          onScroll={onMembersScrolled}
        >
          <SecureLinkMembers
            shareableLinkId={_id}
            members={members}
            isLoading={isMembersLoading}
          />
        </InfiniteScroll>
      )}
    </div>
  );
};

export default SecureLinkAnalytics;
